import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AplicationService } from '@services/aplication/aplication.service';
import { Observable, throwError } from 'rxjs';
import { CreateSegmentDto } from './dtos/create-segment.dto';
import { CampaignsSegmentsRepository } from './campaigns-segments.repository';
import { SegmentDetailResponse, SegmentResponse, SegmentsPaginatedResponse } from '../domain/segment.model';

@Injectable({
    providedIn: 'root',
})
export class CampaignsSegmentsApiService implements CampaignsSegmentsRepository {
    private readonly http = inject(HttpClient);
    private readonly aplicationService = inject(AplicationService);

    /**
     * Builds the roads-scoped base URL and invokes `request` with it. When no aplication
     * is selected, fails fast by returning an Observable error instead of throwing
     * synchronously, so callers' existing `catchError`/`finalize` pipelines still run.
     */
    private withSegmentsBaseUrl<T>(request: (baseUrl: string) => Observable<T>): Observable<T> {
        const roadId = this.aplicationService.selectedRoadId();

        if (!roadId) {
            return throwError(() => new Error('Cannot build segments request URL: no aplication selected'));
        }

        return request(`${environment.url}/prism-admin-campaigns-segments/v2/roads/${roadId}/segments`);
    }

    createSegment(request: CreateSegmentDto): Observable<SegmentResponse> {
        return this.withSegmentsBaseUrl((baseUrl) => this.http.post<SegmentResponse>(baseUrl, request));
    }

    getSegments(page: number, limit: number): Observable<SegmentsPaginatedResponse> {
        let params = new HttpParams();
        if (page) {
            params = params.set('page', page);
        }
        if (limit) {
            params = params.set('limit', limit);
        }

        return this.withSegmentsBaseUrl((baseUrl) =>
            this.http.get<SegmentsPaginatedResponse>(baseUrl, {
                params,
            })
        );
    }

    getSegment(segmentId: string, page: number, limit: number): Observable<SegmentDetailResponse> {
        let params = new HttpParams();
        if (page) {
            params = params.set('page', page);
        }
        if (limit) {
            params = params.set('limit', limit);
        }
        return this.withSegmentsBaseUrl((baseUrl) =>
            this.http.get<SegmentDetailResponse>(`${baseUrl}/${segmentId}/vehicles`, {
                params,
            })
        );
    }
}
